/**
 * Atlas Lens design tokens — CSS custom property names from @diligentcorp/atlas-design-tokens (theme: lens).
 * Token paths verified against dist/themes/lens/css/ and Atlas MCP `search_atlas_tokens_by_path`.
 *
 * Use these `var(--lens-…)` references in static `sx` objects. For runtime-resolved values (e.g. box-shadow
 * in JS), prefer `resolveAtlasLensColors(theme)` from `formFieldSpec.ts`.
 */

type AtlasTokenStringValue =
  | string
  | {
      stringValue?: string;
      layers?: unknown[];
    };

/** Atlas tokens may expose CSS as a plain string or a layered shadow object. */
export function resolveAtlasTokenString(
  value: AtlasTokenStringValue | undefined,
  fallback: string,
): string {
  if (!value) {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.stringValue ?? fallback;
}

/** semantic.* → --lens-semantic-* */
export const ATLAS_LENS_SEMANTIC_VARS = {
  /** semantic.color.type.default */
  typeDefault: 'var(--lens-semantic-color-type-default)',
  /** semantic.color.type.muted */
  typeMuted: 'var(--lens-semantic-color-type-muted)',
  /** semantic.color.type.inverse */
  typeInverse: 'var(--lens-semantic-color-type-inverse)',
  /** semantic.color.surface.default */
  surfaceDefault: 'var(--lens-semantic-color-surface-default)',
  /** semantic.color.surface.subtle */
  surfaceSubtle: 'var(--lens-semantic-color-surface-subtle)',
  /** semantic.color.surface.variant */
  surfaceVariant: 'var(--lens-semantic-color-surface-variant)',
  /** semantic.color.outline.default */
  outlineDefault: 'var(--lens-semantic-color-outline-default)',
  /** semantic.color.outline.active */
  outlineActive: 'var(--lens-semantic-color-outline-active)',
  /** semantic.color.outline.fixed */
  outlineFixed: 'var(--lens-semantic-color-outline-fixed)',
  /** semantic.color.ui.divider.default */
  dividerDefault: 'var(--lens-semantic-color-ui-divider-default)',
  /** semantic.color.ui.scrollbar.handle — Figma thumb #B6B8B9 */
  scrollbarHandle: 'var(--lens-semantic-color-ui-scrollbar-handle)',
  /** semantic.color.ui.focusRing */
  uiFocusRing: 'var(--lens-semantic-color-ui-focus-ring)',
  /** semantic.color.ui.focusInverse */
  uiFocusInverse: 'var(--lens-semantic-color-ui-focus-inverse)',
  /** semantic.shadow.focus — Semantic/Shadow/Focus/Default (outside focus ring) */
  shadowFocus: 'var(--lens-semantic-shadow-focus)',
  /** semantic.shadow.low — Semantic/Shadow/Elevation/Low */
  shadowLow: 'var(--lens-semantic-shadow-low)',
  /** semantic.color.status.error.default */
  statusErrorDefault: 'var(--lens-semantic-color-status-error-default)',
  /** semantic.color.status.error.text */
  statusErrorText: 'var(--lens-semantic-color-status-error-text)',
  /** semantic.color.status.success.background — pale green (green-95) */
  statusSuccessBackground: 'var(--lens-semantic-color-status-success-background)',
  /** semantic.color.status.success.default — bright green #8EE400 (core green-90) */
  statusSuccessDefault: 'var(--lens-semantic-color-status-success-default)',
  /** semantic.color.status.success.content */
  statusSuccessContent: 'var(--lens-semantic-color-status-success-content)',
  /** semantic.color.status.neutral.background.variant — compact alert surface (#E6E6E6) */
  statusNeutralBackgroundVariant: 'var(--lens-semantic-color-status-neutral-background-variant)',
  /** semantic.color.action.primary.default */
  actionPrimaryDefault: 'var(--lens-semantic-color-action-primary-default)',
  /** semantic.color.action.primary.on-primary */
  actionPrimaryOnPrimary: 'var(--lens-semantic-color-action-primary-on-primary)',
  /** semantic.color.action.primary.disabled — disabled contained primary background (#E6E6E6) */
  actionPrimaryDisabled: 'var(--lens-semantic-color-action-primary-disabled)',
  /** semantic.color.action.primary.on-primary-disabled — disabled contained primary label */
  actionPrimaryOnPrimaryDisabled: 'var(--lens-semantic-color-action-primary-on-primary-disabled)',
  /** semantic.color.status.warning.text */
  statusWarningText: 'var(--lens-semantic-color-status-warning-text)',
  /** semantic.color.accent.red.content */
  accentRedContent: 'var(--lens-semantic-color-accent-red-content)',
  /** core.color.brand.primary.diligentRed — required field label suffix (#EE312E) */
  brandDiligentRed: 'var(--lens-core-color-brand-primary-diligent-red)',
  /** semantic.color.accent.blue.background */
  accentBlueBackground: 'var(--lens-semantic-color-accent-blue-background)',
  /** semantic.color.accent.blue.content */
  accentBlueContent: 'var(--lens-semantic-color-accent-blue-content)',
  /** core.color.blue.80 — AI / notification accent strip (#1DD9F9) */
  coreBlue80: 'var(--lens-core-color-blue-80)',
  /** semantic.color.accent.purple.background — translating status pill (#EAB2FF) */
  accentPurpleBackground: 'var(--lens-semantic-color-accent-purple-background)',
  /** semantic.color.accent.purple.content */
  accentPurpleContent: 'var(--lens-semantic-color-accent-purple-content)',
  /** semantic.color.selection.secondary.default */
  selectionSecondaryDefault: 'var(--lens-semantic-color-selection-secondary-default)',
  /** semantic.color.selection.secondary.hover */
  selectionSecondaryHover: 'var(--lens-semantic-color-selection-secondary-hover)',
  /** semantic.color.background.backdrop */
  backgroundBackdrop: 'var(--lens-semantic-color-background-backdrop)',
  /** semantic.color.background.container */
  backgroundContainer: 'var(--lens-semantic-color-background-container)',
  /** semantic.color.background.nav-selection — brand red icon tile (#D3222A) */
  backgroundNavSelection: 'var(--lens-semantic-color-background-nav-selection)',
  /** semantic.layout.xl.maxWidth — page content column (1376px @ xl) */
  layoutXlMaxWidth: 'var(--lens-semantic-layout-xl-max-width)',
  /** core.color.brand.primary.red-2 — Figma Rectangle 3 */
  brandPrimaryRed2: 'var(--lens-core-color-brand-primary-red-2)',
} as const;

/** component.input.* / component.modal.* → --lens-component-* */
export const ATLAS_LENS_COMPONENT_VARS = {
  modalBoxShadow: 'var(--lens-component-modal-box-shadow)',
  modalBorderRadius: 'var(--lens-component-modal-border-radius)',
  modalBackground: 'var(--lens-component-modal-background-color)',
  inputMediumHeight: 'var(--lens-component-input-medium-container-height)',
  inputMediumPaddingX: 'var(--lens-component-input-medium-container-padding-left)',
  inputMediumPaddingY: 'var(--lens-component-input-medium-container-padding-top)',
  inputMediumRadius: 'var(--lens-component-input-medium-border-border-radius)',
  inputOutlinedBg: 'var(--lens-component-input-outlined-default-container-background)',
  inputOutlinedBorder: 'var(--lens-component-input-outlined-default-border-border-color)',
  inputOutlinedBorderHover: 'var(--lens-component-input-outlined-hover-border-border-color)',
  inputOutlinedBorderActive: 'var(--lens-component-input-outlined-active-border-border-color)',
  inputOutlinedBorderError: 'var(--lens-component-input-outlined-error-border-border-color)',
  inputOutlinedText: 'var(--lens-component-input-outlined-default-container-color)',
  inputOutlinedPlaceholder: 'var(--lens-component-input-outlined-default-placeholder-color)',
  checkboxWidth: 'var(--lens-component-checkbox-width)',
  checkboxHeight: 'var(--lens-component-checkbox-height)',
  checkboxRadius: 'var(--lens-component-checkbox-border-radius)',
  checkboxBorderWidth: 'var(--lens-component-checkbox-border-width)',
  checkboxUncheckedBg: 'var(--lens-component-checkbox-unchecked-default-background)',
  checkboxUncheckedBorder: 'var(--lens-component-checkbox-unchecked-default-border-color)',
  checkboxCheckedBg: 'var(--lens-component-checkbox-checked-default-background)',
  checkboxCheckedBorder: 'var(--lens-component-checkbox-checked-default-border-color)',
  checkboxIndicator: 'var(--lens-component-checkbox-indicator-color)',
  menuBoxShadow: 'var(--lens-component-dropdown-container-box-shadow)',
  menuBorderRadius: 'var(--lens-component-dropdown-container-border-radius)',
  menuBackground: 'var(--lens-component-dropdown-container-background-color)',
  buttonSmallHeight: 'var(--lens-component-button-small-height)',
  buttonSmallPaddingX: 'var(--lens-component-button-small-padding-left)',
  buttonSmallGap: 'var(--lens-component-button-small-gap)',
  buttonSmallBorderRadius: 'var(--lens-component-button-small-border-radius)',
  buttonSmallFontSize: 'var(--lens-component-button-small-font-size)',
  buttonSmallLineHeight: 'var(--lens-component-button-small-line-height)',
  buttonSmallFontWeight: 'var(--lens-component-button-small-font-weight)',
  buttonMediumHeight: 'var(--lens-component-button-medium-height)',
  buttonMediumBorderRadius: 'var(--lens-component-button-medium-border-radius)',
  buttonSecondaryDefaultBorder: 'var(--lens-component-button-secondary-default-border-color)',
  buttonSecondaryDefaultBackground: 'var(--lens-component-button-secondary-default-background)',
  buttonSecondaryDefaultColor: 'var(--lens-component-button-secondary-default-color)',
  buttonSecondaryHoverBackground: 'var(--lens-component-button-secondary-hover-background)',
  buttonSecondaryHoverBorder: 'var(--lens-component-button-secondary-hover-border-color)',
  buttonPrimaryDefaultBackground: 'var(--lens-component-button-primary-default-background)',
  buttonPrimaryDefaultColor: 'var(--lens-component-button-primary-default-color)',
  buttonPrimaryHoverBackground: 'var(--lens-component-button-primary-hover-background)',
  buttonPrimaryHoverColor: 'var(--lens-component-button-primary-hover-color)',
  buttonPrimaryDisabledBackground: 'var(--lens-component-button-primary-disabled-background)',
  buttonPrimaryDisabledColor: 'var(--lens-component-button-primary-disabled-color)',
  toastContainerMargin: 'var(--lens-component-toast-container-margin)',
  toastAlertMaxWidth: 'var(--lens-component-toast-alert-max-width)',
  toastAlertMinWidth: 'var(--lens-component-toast-alert-min-width)',
  toastAlertMinHeight: 'var(--lens-component-toast-alert-min-height)',
  toastAlertBorderRadius: 'var(--lens-component-toast-alert-border-radius)',
  toastAlertIconWidth: 'var(--lens-component-toast-alert-icon-width)',
  toastAlertIconMinHeight: 'var(--lens-component-toast-alert-icon-min-height)',
  toastAlertIconBorderRadius: 'var(--lens-component-toast-alert-icon-border-radius)',
  /** component.list-item.content.media — uploader / list file row thumbnail */
  listItemContentMediaWidth: 'var(--lens-component-list-item-content-media-width)',
  listItemContentMediaHeight: 'var(--lens-component-list-item-content-media-height)',
  listItemContentMediaBorderRadius: 'var(--lens-component-list-item-content-media-border-radius)',
  cardContainerBorderRadius: 'var(--lens-component-card-container-border-radius)',
  cardContainerBorderColor: 'var(--lens-component-card-container-border-color)',
  cardMediaBorderRadius: 'var(--lens-component-card-media-border-radius)',
} as const;

/** component.toggle.* → --lens-component-toggle-* (Atlas Lens switch / toggle). */
export const ATLAS_LENS_TOGGLE_VARS = {
  largeBarWidth: 'var(--lens-component-toggle-large-bar-width)',
  largeBarHeight: 'var(--lens-component-toggle-large-bar-height)',
  largeBarPadding: 'var(--lens-component-toggle-large-bar-padding)',
  largeBarBorderRadius: 'var(--lens-component-toggle-large-bar-border-radius)',
  largeKnobWidth: 'var(--lens-component-toggle-large-knob-width)',
  largeKnobHeight: 'var(--lens-component-toggle-large-knob-height)',
  checkedDefaultBarBackground:
    'var(--lens-component-toggle-checked-default-bar-background)',
  checkedDefaultBarBorderColor:
    'var(--lens-component-toggle-checked-default-bar-border-color)',
  checkedDefaultKnobBackground:
    'var(--lens-component-toggle-checked-default-knob-background-color)',
  uncheckedDefaultBarBackground:
    'var(--lens-component-toggle-unchecked-default-bar-background)',
  uncheckedDefaultBarBorderColor:
    'var(--lens-component-toggle-unchecked-default-bar-border-color)',
  uncheckedDefaultKnobBackground:
    'var(--lens-component-toggle-unchecked-default-knob-background-color)',
  uncheckedDefaultKnobBorderColor:
    'var(--lens-component-toggle-unchecked-default-knob-border-color)',
  checkedDefaultKnobBorderColor:
    'var(--lens-component-toggle-checked-default-knob-border-color)',
} as const;

/** Lens spacing expressions for layouts without a single token (Figma-measured). */
export const ATLAS_LENS_SPACING_EXPR = {
  /** Course/media card thumbnail — Figma 160px = spacing-8×2 + spacing-4 */
  courseCardMediaHeight: 'calc(var(--lens-core-spacing-8) * 2 + var(--lens-core-spacing-4))',
  /** Loading placeholder min-height — spacing-8×3 + spacing-6 */
  loadingPlaceholderMinHeight: 'calc(var(--lens-core-spacing-8) * 3 + var(--lens-core-spacing-6))',
} as const;

/** component.aiChatUi.panel.* — CSS custom property names for overrides on a host element. */
export const ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS = {
  dockedWidth: '--lens-component-ai-chat-ui-panel-docked-width',
  dockedBorderInlineStartColor:
    '--lens-component-ai-chat-ui-panel-docked-border-inline-start-color',
  dockedBorderInlineStartWidth:
    '--lens-component-ai-chat-ui-panel-docked-border-inline-start-width',
  minimizedInsetInlineEnd: '--lens-component-ai-chat-ui-panel-minimized-inset-inline-end',
  minimizedInsetBlockEnd: '--lens-component-ai-chat-ui-panel-minimized-inset-block-end',
  headerMaximizedPaddingInline:
    '--lens-component-ai-chat-ui-panel-header-maximized-padding-inline',
  headerMaximizedPaddingBlockEnd:
    '--lens-component-ai-chat-ui-panel-header-maximized-padding-block-end',
  headerMaximizedTitleFontSize:
    '--lens-component-ai-chat-ui-panel-header-maximized-title-font-size',
  headerMaximizedTitleFontWeight:
    '--lens-component-ai-chat-ui-panel-header-maximized-title-font-weight',
  headerMaximizedTitleLineHeight:
    '--lens-component-ai-chat-ui-panel-header-maximized-title-line-height',
  disclaimerPaddingInline: '--lens-component-ai-chat-ui-panel-disclaimer-padding-inline',
  disclaimerPaddingBlock: '--lens-component-ai-chat-ui-panel-disclaimer-padding-block',
} as const;

/** component.aiChatUi.panel.* → var(--lens-component-ai-chat-ui-panel-*) for use in styles. */
export const ATLAS_LENS_AI_CHAT_PANEL_VARS = {
  dockedWidth: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.dockedWidth})`,
  dockedBorderInlineStartColor: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.dockedBorderInlineStartColor})`,
  dockedBorderInlineStartWidth: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.dockedBorderInlineStartWidth})`,
  minimizedInsetInlineEnd: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.minimizedInsetInlineEnd})`,
  minimizedInsetBlockEnd: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.minimizedInsetBlockEnd})`,
  headerMaximizedPaddingInline: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.headerMaximizedPaddingInline})`,
  headerMaximizedPaddingBlockEnd: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.headerMaximizedPaddingBlockEnd})`,
  headerMaximizedTitleFontSize: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.headerMaximizedTitleFontSize})`,
  headerMaximizedTitleFontWeight: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.headerMaximizedTitleFontWeight})`,
  headerMaximizedTitleLineHeight: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.headerMaximizedTitleLineHeight})`,
  disclaimerPaddingInline: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.disclaimerPaddingInline})`,
  disclaimerPaddingBlock: `var(${ATLAS_LENS_AI_CHAT_PANEL_CSS_VARS.disclaimerPaddingBlock})`,
} as const;

/**
 * Semantic aliases used across forms and onboarding — maps to Lens CSS variables.
 * Matches pre-refactor formFieldSpec.ts token names (CEE-295 / Figma).
 */
export const ATLAS_LENS_COLORS = {
  typeDefault: ATLAS_LENS_SEMANTIC_VARS.typeDefault,
  typeMuted: ATLAS_LENS_SEMANTIC_VARS.typeMuted,
  typeInverse: ATLAS_LENS_SEMANTIC_VARS.typeInverse,
  surfaceDefault: ATLAS_LENS_SEMANTIC_VARS.surfaceDefault,
  surfaceSubtle: ATLAS_LENS_SEMANTIC_VARS.surfaceSubtle,
  outlineDefault: ATLAS_LENS_SEMANTIC_VARS.outlineDefault,
  outlineActive: ATLAS_LENS_SEMANTIC_VARS.outlineActive,
  outlineFixed: ATLAS_LENS_SEMANTIC_VARS.outlineFixed,
  actionFormOutline: 'var(--lens-semantic-color-action-form-outline)',
  focusRing:
    '0 0 0 1px var(--lens-semantic-color-ui-focus-inverse), 0 0 0 3px var(--lens-semantic-color-ui-focus-ring)',
  /** Required label asterisk — not status error text. */
  accentRed: ATLAS_LENS_SEMANTIC_VARS.brandDiligentRed,
  statusWarningContent: 'var(--lens-semantic-color-status-warning-content)',
  divider: ATLAS_LENS_SEMANTIC_VARS.dividerDefault,
  checkboxOutline: 'var(--lens-semantic-color-action-form-outline)',
  selectedBackground: ATLAS_LENS_SEMANTIC_VARS.selectionSecondaryDefault,
  selectedBorder: 'var(--lens-semantic-color-selection-secondary-selection-indicator)',
  selectedBackgroundHover: ATLAS_LENS_SEMANTIC_VARS.selectionSecondaryHover,
  unselectedBackground: ATLAS_LENS_SEMANTIC_VARS.surfaceDefault,
  unselectedBorder: ATLAS_LENS_SEMANTIC_VARS.outlineFixed,
  surfaceVariant: ATLAS_LENS_SEMANTIC_VARS.surfaceVariant,
  backgroundBackdrop: ATLAS_LENS_SEMANTIC_VARS.backgroundBackdrop,
  backgroundContainer: ATLAS_LENS_SEMANTIC_VARS.backgroundContainer,
  backgroundNavSelection: ATLAS_LENS_SEMANTIC_VARS.backgroundNavSelection,
  brandPrimaryDiligentRed: ATLAS_LENS_SEMANTIC_VARS.brandDiligentRed,
  brandPrimaryRed2: ATLAS_LENS_SEMANTIC_VARS.brandPrimaryRed2,
} as const;

/** @deprecated Use ATLAS_LENS_COMPONENT_VARS */
export const ATLAS_LENS_COMPONENT_TOKENS = ATLAS_LENS_COMPONENT_VARS;
